import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  SearchIcon,
  FilterIcon,
  DocumentTextIcon,
  UserIcon,
} from "@heroicons/react/outline";
import {
  getPendingPauseRequestsThunk,
  approvePauseThunk,
  rejectPauseThunk,
  clearPauseData,
} from "../../../../store/thunks/jewelleryPlan/planThunks";
import Badge from "../../../../components/common/Badge";
import Button from "../../../../components/common/Button";
import { notifySuccess, notifyError } from "../../../../utils/notification";

const PendingPauseRequestsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ Safe selectors with fallbacks
  const pendingRequests = useSelector(
    (state) => state.jewelleryPlan?.plans?.pendingPauseRequests || []
  );
  const loading = useSelector(
    (state) => state.jewelleryPlan?.plans?.pendingPauseLoading || false
  );
  const pauseApproved = useSelector(
    (state) => state.jewelleryPlan?.plans?.pauseApproved || false
  );
  const pauseRejected = useSelector(
    (state) => state.jewelleryPlan?.plans?.pauseRejected || false
  );
  const pauseError = useSelector(
    (state) => state.jewelleryPlan?.plans?.pauseError || null
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    branchId: null,
    reason: null,
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Load pending requests on mount
  useEffect(() => {
    dispatch(getPendingPauseRequestsThunk(filters));
  }, [dispatch, filters]);

  // Refresh list after approval/rejection
  useEffect(() => {
    if (pauseApproved || pauseRejected) {
      dispatch(getPendingPauseRequestsThunk(filters));
      notifySuccess(
        pauseApproved
          ? "Pause request approved successfully"
          : "Pause request rejected"
      );
      setShowApproveModal(false);
      setShowRejectModal(false);
      setActionNotes("");
      setSelectedRequest(null);
      setTimeout(() => {
        dispatch(clearPauseData());
      }, 2000);
    }
  }, [pauseApproved, pauseRejected, dispatch, filters]);

  // Handle errors
  useEffect(() => {
    if (pauseError) {
      notifyError(pauseError);
      setActionLoading(false);
    }
  }, [pauseError]);

  // Add this helper function at the top of your component (before the return)

  const safeFormatDate = (dateValue, formatString = "dd MMM yyyy") => {
    if (!dateValue) return "N/A";

    try {
      const date = new Date(dateValue);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
      return format(date, formatString);
    } catch (error) {
      console.error("Date formatting error:", error, dateValue);
      return "Invalid Date";
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setShowApproveModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
  };

  const confirmApprove = async () => {
    if (!actionNotes.trim()) {
      notifyError("Please enter approval notes");
      return;
    }

    setActionLoading(true);

    try {
      const result = await dispatch(
        approvePauseThunk({
          pauseId: selectedRequest.pauseId,
          approvalData: {
            approvalNotes: actionNotes,
          },
        })
      );

      if (approvePauseThunk.rejected.match(result)) {
        notifyError(result.payload || "Failed to approve pause request");
      }
    } catch (error) {
      notifyError(error.message || "Failed to approve pause request");
    } finally {
      setActionLoading(false);
    }
  };

  const confirmReject = async () => {
    if (!actionNotes.trim()) {
      notifyError("Please enter rejection reason");
      return;
    }

    setActionLoading(true);

    try {
      const result = await dispatch(
        rejectPauseThunk({
          pauseId: selectedRequest.pauseId,
          rejectionData: {
            rejectionReason: actionNotes,
          },
        })
      );

      if (rejectPauseThunk.rejected.match(result)) {
        notifyError(result.payload || "Failed to reject pause request");
      }
    } catch (error) {
      notifyError(error.message || "Failed to reject pause request");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ Safe filtering with fallback
  const filteredRequests = (pendingRequests || []).filter((request) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      request.planNumber?.toLowerCase().includes(searchLower) ||
      request.customer?.name?.toLowerCase().includes(searchLower) ||
      request.customer?.phoneNumber?.includes(searchTerm)
    );
  });

  const getReasonLabel = (reason) => {
    const labels = {
      FINANCIAL_TEMPORARY: "Temporary Financial Issue",
      MEDICAL_EMERGENCY: "Medical Emergency",
      JOB_LOSS: "Job Loss",
      TRAVEL: "Travel",
      FAMILY_EMERGENCY: "Family Emergency",
      MATERNITY: "Maternity",
      EDUCATION: "Education",
      BUSINESS_LOSS: "Business Loss",
      NATURAL_DISASTER: "Natural Disaster",
      OTHER: "Other",
    };
    return labels[reason] || reason;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Pending Pause Requests
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Review and approve or reject plan pause requests
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Pending</p>
            <p className="text-3xl font-bold text-blue-600">
              {filteredRequests.length}
            </p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 gap-4">
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by plan number, customer name, or phone..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <FilterIcon className="h-5 w-5 mr-2" />
            Filters
          </button>
        </div>
      </div>

      {/* Requests List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No pending pause requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.pauseId}
              className="bg-white shadow rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                {/* Request Info */}
                <div className="flex-1 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {request.planNumber}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Requested on:{" "}
                        {safeFormatDate(
                          new Date(request.pauseDetails?.requestedAt),
                          "dd MMM yyyy, HH:mm"
                        )}
                      </p>
                    </div>
                    <Badge variant="warning" size="sm">
                      Pending Approval
                    </Badge>
                  </div>

                  {/* Customer Info */}
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {request.customer?.name || "N/A"}
                        </p>
                        <p className="text-xs text-gray-600">
                          {request.customer?.phoneNumber || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Branch: <strong>{request.branch?.name || "N/A"}</strong>
                    </div>
                  </div>

                  {/* Pause Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-600">Reason</p>
                      <p className="text-sm font-medium text-gray-900">
                        {getReasonLabel(request.pauseDetails?.reason)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Duration</p>
                      <p className="text-sm font-medium text-gray-900">
                        {request.pauseDetails?.monthsPaused} month(s)
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Start Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {request.pauseDetails?.startDate
                          ? format(
                              new Date(request.pauseDetails.startDate),
                              "dd MMM yyyy"
                            )
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">End Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {request.pauseDetails?.endDate
                          ? format(
                              new Date(request.pauseDetails.endDate),
                              "dd MMM yyyy"
                            )
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Reason Details */}
                  {request.pauseDetails?.reasonDetails && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-medium text-blue-900 mb-1">
                        Detailed Explanation:
                      </p>
                      <p className="text-sm text-blue-800">
                        {request.pauseDetails.reasonDetails}
                      </p>
                    </div>
                  )}

                  {/* Plan Metrics */}
                  <div className="flex items-center space-x-6 text-sm">
                    <div>
                      <span className="text-gray-600">Months Completed: </span>
                      <span className="font-medium text-gray-900">
                        {request.planMetrics?.monthsCompleted || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Wallet Value: </span>
                      <span className="font-medium text-green-600">
                        ₹
                        {(
                          request.planMetrics?.walletValue || 0
                        ).toLocaleString()}
                      </span>
                    </div>
                    {(request.planMetrics?.walletGrams || 0) > 0 && (
                      <div>
                        <span className="text-gray-600">Gold: </span>
                        <span className="font-medium text-yellow-600">
                          {request.planMetrics.walletGrams} grams
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Documents */}
                  {request.pauseDetails?.documentsProvided?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-700 mb-2">
                        Supporting Documents:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {request.pauseDetails.documentsProvided.map(
                          (doc, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              {doc}
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-2 lg:w-48">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleApprove(request)}
                    className="w-full"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleReject(request)}
                    className="w-full"
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      navigate(`/jewellery/plans/${request.planId}`)
                    }
                    className="w-full"
                  >
                    View Plan
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Approve Pause Request
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Plan: <strong>{selectedRequest.planNumber}</strong>
              <br />
              Duration:{" "}
              <strong>
                {selectedRequest.pauseDetails?.monthsPaused} month(s)
              </strong>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Approval Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={4}
                placeholder="Enter reason for approval..."
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-green-500 focus:border-green-500"
                disabled={actionLoading}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowApproveModal(false);
                  setActionNotes("");
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={confirmApprove}
                loading={actionLoading}
                disabled={actionLoading}
              >
                {actionLoading ? "Approving..." : "Confirm Approval"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Reject Pause Request
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Plan: <strong>{selectedRequest.planNumber}</strong>
              <br />
              Are you sure you want to reject this pause request?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                rows={4}
                placeholder="Enter reason for rejection..."
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-red-500 focus:border-red-500"
                disabled={actionLoading}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRejectModal(false);
                  setActionNotes("");
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmReject}
                loading={actionLoading}
                disabled={actionLoading}
              >
                {actionLoading ? "Rejecting..." : "Confirm Rejection"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingPauseRequestsPage;
